// @flow
import React from 'react';
import injectSheet from 'react-jss';
import type { SubcomponentEditor } from 'shared/components/EditorContext';
import useScrollToId from 'shared/hooks/useScrollToId';
import FlexibleCell from './FlexibleCell';

type Props = {
  classes: Object,
  section_tag: string,
  padding_all: {
    menu_bar_padding: boolean,
    use_mobile_padding: boolean,
    padding_container_top: string,
    padding_container_bottom: string,
    horizontal_outer_padding: string,
    horizontal_inner_padding: string,
    vertical_inner_padding: string,
    padding_all_tablet: {
      padding_container_top_tablet: string,
      padding_container_bottom_tablet: string,
      horizontal_outer_padding_tablet: string,
      horizontal_inner_padding_tablet: string,
      vertical_inner_padding_tablet: string
    },
    padding_all_mobile: {
      padding_container_top_tablet: string,
      padding_container_bottom_tablet: string,
      horizontal_outer_padding_tablet: string,
      horizontal_inner_padding_tablet: string,
      vertical_inner_padding_tablet: string
    }
  },
  height_all: {
    height_percentage: string,
    height_minimum: string,
    height_maximum: string,
    height_all_tablet: {
      height_percentage_tablet: string,
      height_minimum_tablet: string,
      height_maximum_tablet: string,
    },
    height_all_mobile: {
      height_percentage_mobile: string,
      height_minimum_mobile: string,
      height_maximum_mobile: string,
    }
  },
  background: {
    use_background_color: boolean,
    color: string,
    content_type: string,
    content: string,
    mobile_content: string,
    tablet_content: string,
    position: string,
    background_to_image_mobile: boolean
  },
  column_layout: {
    mobile_keep_direction: boolean,
    direction: string,
    count: string,
    variant: string,
    remove_z_index: boolean,
    remove_column_space: boolean,
    position_column: string,
    position_row: string,
    fillSpace: boolean,
    fillSpaceIndex: string,
  },
  hidden_controls: {
    hide_on_desktop: boolean,
    hide_on_tablet: boolean,
    hide_on_mobile: boolean,
  },
  width_controls: {
    max_width: boolean,
  },
  subcomponents: Array<?Object>,
  isEditing: boolean,
  editor: SubcomponentEditor,
  _id: string
};

const FlexibleColumns = (props: Props) => {
  const {
    classes: c,
    section_tag: sectionTag,
    padding_all: paddingAll,
    background: {
      content_type: bgContentType,
      content: bgContent,
      mobile_content: bgMobileContent,
      background_to_image_mobile: bgToImageMobile
    },
    column_layout: {
      mobile_keep_direction: mobileKeepDirection,
      direction: layoutDirection,
      count: _columnCount,
      variant: columnVariant,
      remove_column_space: removeColumnSpace,
      fillSpace,
      fillSpaceIndex
    },
    hidden_controls: hiddenControls,
    subcomponents: _subcomponents = [],
    isEditing,
    editor,
    _id
  } = props;

  const containerRef = useScrollToId(sectionTag);

  const subcomponents = _subcomponents.slice();
  const columnCount = parseInt(_columnCount, 10);

  // Ensure that all columns are rendered, even if empty
  while (subcomponents.length < columnCount) {
    subcomponents.push(null);
  }

  const videoBlock = bgContentType === 'Video' && bgContent && (
    <div className={c.videoContainer}>
      <video
        playsInline
        autoPlay
        muted
        loop
        className={c.video}
      >
        <source src={bgContent} type="video/mp4" />
      </video>
    </div>
  );

  return (
    <React.Fragment>
      {videoBlock}
      <div
        className={c.container}
        id={sectionTag}
        ref={containerRef}
      >
        <div className={c.flex}>
          {
            bgContentType === 'Image' &&
            bgToImageMobile &&
            <img
              className={c.mobileImage}
              src={bgMobileContent}
            />
          }
          {
            subcomponents && subcomponents.slice(0, columnCount).map((o, i) => (
              <FlexibleCell
                key={i}
                {...{
                  removeColumnSpace,
                  paddingAll,
                  mobileKeepDirection,
                  layoutDirection,
                  columnCount,
                  columnVariant,
                  hiddenControls,
                  isEditing,
                  editor,
                  fillSpace: fillSpace && parseInt(fillSpaceIndex) === i + 1
                }}
                parentId={_id}
                component={o}
                componentIndex={i}
              />
            ))
          }
        </div>
      </div>
    </React.Fragment>
  );
};

// Converts unit to vh for height, or px for min/max height
const getHeight = height => (height ? height.replace(/(%|vh)$/, '').concat('vh') : 'auto');
const getMinHeight = height => (height ? height.replace(/(px)$/, '').concat('px') : '0');
const getMaxHeight = height => (height ? height.replace(/(px)$/, '').concat('px') : 'none');

const getDesktopFlexDirection = (props) => {
  const { column_layout: { direction }} = props;

  return direction === 'Single Column' ? 'column' : 'row';
}
const getMobileFlexDirection = (props) => {
  const { column_layout: { mobile_keep_direction }} = props;

  if (mobile_keep_direction) {
    return getDesktopFlexDirection(props);
  }

  return 'column';
}

const getLayoutPositionColumn = (props) => {
  if (props?.column_layout?.direction === 'Single Column' && props?.column_layout?.remove_column_space) {
    return props?.column_layout?.position_column;
  }
  // Default position_column
  return 'stretch';
}

const getLayoutPositionRow = (props) => {
  if (props?.column_layout?.direction === 'Single Row' && props?.column_layout?.remove_column_space) {
    return props?.column_layout?.position_row;
  }
  // Default position_row
  return 'flex-start';
}

const getLayoutPosition = (props) => {
  const { column_layout: { direction }} = props;
  return direction === 'Single Column' ? getLayoutPositionColumn(props) : getLayoutPositionRow(props);
}

const styles = {
  container: {
    scrollSnapAlign: (({ scrollSnapAlign }) => scrollSnapAlign),
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundColor: ({ background }) => ((background?.use_background_color && background?.color) || 'transparent'),
    backgroundPosition: ({ background }) => background?.content_type === 'Image' ? background?.position || 'CENTER CENTER' : '0% 0%',
    justifyContent: (props) => getLayoutPosition(props),

    // Remove background if 'background_to_image_mobile' enabled, using mobile-specific image
    '@media(max-width: 576px)': {
      display: ({ editor, hidden_controls: { hide_on_mobile } = {}}) => !editor && hide_on_mobile ? 'none' : 'block',
      backgroundImage: ({ background }) =>
        background?.content_type === 'Image' &&
        !background?.background_to_image_mobile ?
        `url(${background?.mobile_content || background?.content})` : 'transparent',
    },
    '@media(min-width: 769px)': {
      display: ({ editor, hidden_controls: { hide_on_desktop } = {}}) => !editor && hide_on_desktop ? 'none' : 'block',
      backgroundImage: ({ background }) => background?.content_type === 'Image' ? `url(${background?.content})` : 'transparent',
    },
    '@media(min-width: 577px) and (max-width: 768px)': {
      display: ({ editor, hidden_controls: { hide_on_tablet } = {}}) => !editor && hide_on_tablet ? 'none' : 'block',
      backgroundImage: ({ background }) => background?.content_type === 'Image' ? `url(${background?.tablet_content || background?.content})` : 'transparent',
    },

    width: '100%',
    zIndex: (props) => props.column_layout?.remove_z_index ? 'unset' : '3',
    position: 'relative',
    // Add padding in Page Editor
    '.js-controls > &': {
      zIndex: '3', // for designer drag and drop
      paddingTop: '50px',
      marginBottom: '50px'
    },
  },
  mobileImage: {
    '@media(min-width: 577px)': {
      display: 'none'
    },
  },
  flex: {
    // Align items or justify content if remove space between columns is enabled
    alignItems: (props) => props?.column_layout?.direction === 'Single Column' && getLayoutPosition(props),
    justifyContent: (props) => props?.column_layout?.direction === 'Single Row' && getLayoutPosition(props),
    paddingTop: ({ padding_all: { menu_bar_padding, padding_container_top }}) => (menu_bar_padding ? '84px' : (padding_container_top || '0px')),
    paddingBottom: ({ padding_all: { padding_container_bottom }}) => (padding_container_bottom || '0px'),
    height: ({ height_all: { height_percentage } = {}}) => getHeight(height_percentage),
    minHeight: ({ height_all: { height_minimum } = {}}) => getMinHeight(height_minimum),
    maxHeight: ({ height_all: { height_maximum } = {}}) => getMaxHeight(height_maximum),
    width: '100%',
    display: 'flex',
    maxWidth: (props) => (props?.width_controls?.max_width) ? '100%' : '1596px',
    margin: 'auto',
    '@media(max-width: 576px)': {
      flexDirection: (props) => getMobileFlexDirection(props),
      paddingTop: ({ padding_all: { menu_bar_padding, padding_container_top, padding_all_mobile: { padding_container_top_mobile } = {}} = {}}) => (menu_bar_padding ? '84px' : (padding_container_top_mobile || padding_container_top)),
      paddingBottom: ({ padding_all: { padding_container_bottom, padding_all_mobile: { padding_container_bottom_mobile } = {}} = {}}) => (padding_container_bottom_mobile || padding_container_bottom),
      height: ({ height_all: { height_percentage, height_all_mobile: { height_percentage_mobile } = {}}  = {}}) => getHeight(height_percentage_mobile || height_percentage),
      minHeight: ({ height_all: { height_minimum, height_all_mobile: { height_minimum_mobile } = {}} = {}}) => getMinHeight(height_minimum_mobile || height_minimum),
      maxHeight: ({ height_all: { height_maximum, height_all_mobile: { height_maximum_mobile } = {}} = {}}) => getMaxHeight(height_maximum_mobile || height_maximum),
    },
    '@media(min-width: 577px) and (max-width: 768px) and (orientation: portrait)': {
      paddingTop: ({ padding_all: { menu_bar_padding, padding_container_top, padding_all_tablet: { padding_container_top_tablet } = {}} = {}}) => (menu_bar_padding ? '84px' : (padding_container_top_tablet || padding_container_top)),
      paddingBottom: ({ padding_all: { padding_container_bottom, padding_all_tablet: { padding_container_bottom_tablet } = {}} = {}}) => (padding_container_bottom_tablet || padding_container_bottom),
      height: ({ height_all: { height_percentage, height_all_tablet: { height_percentage_tablet } = {}} = {}}) => getHeight(height_percentage_tablet || height_percentage),
      minHeight: ({ height_all: { height_minimum, height_all_tablet: { height_minimum_tablet } = {}} = {}}) => getMinHeight(height_minimum_tablet || height_minimum),
      maxHeight: ({ height_all: { height_maximum, height_all_tablet: { height_maximum_tablet } = {}} = {}}) => getMaxHeight(height_maximum_tablet || height_maximum),
    },
    '@media(min-width: 577px)': {
      flexDirection: (props) => getDesktopFlexDirection(props)
    }
  },
  videoContainer: {
    width: '100%',
    position: 'absolute',
    overflow: 'hidden'
  },
  video: {
    objectFit: 'cover',
    backgroundSize: 'cover',
    width: '102%',
    height: '100%',
    visibility: 'inherit',
    opacity: '1',
    top: '0px',
    left: '-2px',
    display: 'block'
  }
};

export default injectSheet(styles)(FlexibleColumns);
